import React, { useEffect, useRef, useState } from 'react';
import { InsightModal } from '../../components/dashboard/myData/MyWidgetInsights';
import { usd, tone } from '../../components/dashboard/myData/myWidgetUi';
import {
  createMyStrategy,
  deleteMyStrategy,
  getMyStrategyWebhook,
  importMyStrategyCsv,
  myStrategyWebhookUrl,
  newMyStrategyWebhook,
  removeMyStrategyWebhook,
  sendMyStrategyTest,
  updateMyStrategy,
} from '../../services/personalStrategiesApi';

/*
 * "My strategies": the user's own strategies from a backtest CSV or a webhook.
 * Personal only: they show in this user's Results and Simulator and nowhere
 * else. Adding a strategy to a company is done by its Brand Admins.
 */

const FORMAT_LABEL = {
  'tradesviz-trades': 'TradesViz trades',
  'tradesviz-executions': 'TradesViz executions',
  'tradingview-trades': 'TradingView trades',
  'ninjatrader-executions': 'NinjaTrader executions',
  normalized: 'Trade list',
  'daily-pnl': 'Daily P&L',
  'daily-equity': 'Daily balance',
};
const formatLabel = (f) => FORMAT_LABEL[f] || (f ? String(f) : '');

const EXAMPLES = [
  {
    label: 'A closed trade',
    body: { symbol: 'NQ', side: 'long', quantity: 1, entry_price: 21000.25, exit_price: 21012.5, pnl: 245, time: '{{timenow}}' },
  },
  {
    label: 'A closed trade (we work out the P&L)',
    body: { symbol: 'MES', side: 'short', quantity: 2, entry_price: 5900, exit_price: 5890, multiplier: 5, time: '{{timenow}}' },
  },
  {
    label: 'A day’s result',
    body: { date: '2026-10-16', pnl: 412.5 },
  },
  {
    label: 'A day’s closing balance',
    body: { date: '2026-10-16', balance: 51240 },
  },
];

function CsvImport({ strategy, onDone }) {
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hasData = strategy.tradeCount + strategy.dayCount > 0;

  const pick = async (file) => {
    if (!file) return;
    setError('');
    setPreview(null);
    setFileName(file.name);
    if (file.size > 10 * 1024 * 1024) { setError('That file is over 10 MB.'); return; }
    const text = await file.text();
    setCsv(text);
    setBusy(true);
    try {
      setPreview(await importMyStrategyCsv(strategy.id, text, { preview: true }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const run = async (mode) => {
    setBusy(true);
    setError('');
    try {
      await importMyStrategyCsv(strategy.id, csv, { mode });
      onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="mys-panel">
      <p className="mys-help">
        Upload a backtest or trade export. We read TradesViz, TradingView “List of trades”, NinjaTrader executions,
        any trade list with a date and P&amp;L, or a daily file with a date and P&amp;L or balance.
      </p>
      <label className="mys-file">
        <input type="file" accept=".csv,text/csv" onChange={(e) => pick(e.target.files?.[0])} />
        <span>{fileName || 'Choose a CSV file'}</span>
      </label>
      {busy && !preview ? <p className="mys-muted">Reading the file…</p> : null}
      {error ? <p className="mys-error" role="alert">{error}</p> : null}
      {preview ? (
        <div className="mys-preview">
          <div><span>Read as</span><b>{formatLabel(preview.format)}</b></div>
          <div><span>{preview.trades ? 'Trades' : 'Days'}</span><b>{(preview.trades || preview.days).toLocaleString()}</b></div>
          <div><span>Dates</span><b>{preview.firstDate} → {preview.lastDate}</b></div>
          <div><span>Net P&amp;L</span><b className={tone(preview.netPnl)}>{usd(preview.netPnl, { sign: true })}</b></div>
        </div>
      ) : null}
      {preview ? (
        <div className="mys-actions">
          <button type="button" className="mys-btn is-primary" disabled={busy} onClick={() => run('replace')}>
            {hasData ? 'Replace earlier CSV data' : 'Import'}
          </button>
          {hasData ? <button type="button" className="mys-btn" disabled={busy} onClick={() => run('append')}>Add to it</button> : null}
        </div>
      ) : null}
      {hasData && preview ? <p className="mys-muted">Webhook data is kept either way.</p> : null}
    </div>
  );
}

function WebhookPanel({ strategy, onChange }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [example, setExample] = useState(0);

  useEffect(() => {
    let live = true;
    getMyStrategyWebhook(strategy.id)
      .then((r) => { if (live) { setToken(r.token || null); setLoading(false); } })
      .catch((err) => { if (live) { setError(err.message); setLoading(false); } });
    return () => { live = false; };
  }, [strategy.id]);

  const act = async (fn, okNote) => {
    setBusy(true);
    setError('');
    setNote('');
    try {
      await fn();
      if (okNote) setNote(okNote);
      onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const turnOn = () => act(async () => setToken((await newMyStrategyWebhook(strategy.id)).token));
  const regenerate = () => {
    if (!window.confirm('Make a new URL? The old one stops working right away.')) return;
    act(async () => setToken((await newMyStrategyWebhook(strategy.id)).token), 'New URL made. Update it wherever you send alerts from.');
  };
  const turnOff = () => {
    if (!window.confirm('Turn off this webhook? Messages sent to it will be rejected. Data already received is kept.')) return;
    act(async () => { await removeMyStrategyWebhook(strategy.id); setToken(null); });
  };
  const test = () => act(() => sendMyStrategyTest(token), 'Test received. The webhook is working (nothing was saved).');
  const copy = (text) => navigator.clipboard?.writeText(text).then(() => setNote('Copied.')).catch(() => {});

  if (loading) return <div className="mys-panel"><p className="mys-muted">Loading…</p></div>;
  if (!token) {
    return (
      <div className="mys-panel">
        <p className="mys-help">
          Send trades or daily results as they happen, from a TradingView alert, NinjaTrader, or any tool that can post JSON to a URL.
        </p>
        {error ? <p className="mys-error" role="alert">{error}</p> : null}
        <div className="mys-actions">
          <button type="button" className="mys-btn is-primary" disabled={busy} onClick={turnOn}>Turn on webhook</button>
        </div>
      </div>
    );
  }
  const url = myStrategyWebhookUrl(token);
  const body = JSON.stringify(EXAMPLES[example].body, null, 2);
  return (
    <div className="mys-panel">
      <p className="mys-help">Post JSON to this URL. Keep it private: anyone with it can add data to this strategy.</p>
      <div className="mys-url">
        <code>{url}</code>
        <button type="button" className="mys-btn" onClick={() => copy(url)}>Copy</button>
      </div>
      <div className="mys-status">
        {strategy.webhookLastAt
          ? <>Last message {new Date(strategy.webhookLastAt).toLocaleString()} · {strategy.webhookCount.toLocaleString()} received</>
          : <>Nothing received yet.</>}
      </div>
      <div className="mys-examples">
        <div className="mydw-pills" role="tablist">
          {EXAMPLES.map((ex, i) => (
            <button key={ex.label} type="button" className={i === example ? 'active' : ''} onClick={() => setExample(i)}>{ex.label}</button>
          ))}
        </div>
        <pre>{body}</pre>
        <button type="button" className="mys-btn" onClick={() => copy(body)}>Copy example</button>
      </div>
      <p className="mys-muted">
        One message per trade or per day. Sending a day’s balance again replaces that day. Up to 120 messages a minute.
      </p>
      {error ? <p className="mys-error" role="alert">{error}</p> : null}
      {note ? <p className="mys-note">{note}</p> : null}
      <div className="mys-actions">
        <button type="button" className="mys-btn is-primary" disabled={busy} onClick={test}>Send a test</button>
        <button type="button" className="mys-btn" disabled={busy} onClick={regenerate}>New URL</button>
        <button type="button" className="mys-btn is-danger" disabled={busy} onClick={turnOff}>Turn off</button>
      </div>
    </div>
  );
}

function StrategyRow({ strategy, onChanged }) {
  const [open, setOpen] = useState('');
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(strategy.name);
  const [capital, setCapital] = useState(String(strategy.startingCapital));
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      await updateMyStrategy(strategy.id, { name, startingCapital: Number(capital) });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete “${strategy.name}” and all of its data? This can’t be undone.`)) return;
    try {
      await deleteMyStrategy(strategy.id);
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };
  const count = strategy.tradeCount
    ? `${strategy.tradeCount.toLocaleString()} trades`
    : strategy.dayCount ? `${strategy.dayCount.toLocaleString()} days` : 'No data yet';

  return (
    <li className="mys-row">
      <div className="mys-row-head">
        {editing ? (
          <div className="mys-edit">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label="Name" />
            <label>Starting balance <input type="number" min="1" value={capital} onChange={(e) => setCapital(e.target.value)} /></label>
            <button type="button" className="mys-btn is-primary" onClick={save}>Save</button>
            <button type="button" className="mys-btn" onClick={() => { setEditing(false); setName(strategy.name); setCapital(String(strategy.startingCapital)); }}>Cancel</button>
          </div>
        ) : (
          <div className="mys-row-title">
            <strong>{strategy.name}</strong>
            <span className="mys-muted">
              {count}
              {strategy.firstDate ? ` · ${strategy.firstDate} → ${strategy.lastDate}` : ''}
              {` · starts at ${usd(strategy.startingCapital)}`}
              {strategy.hasWebhook ? ' · webhook on' : ''}
            </span>
          </div>
        )}
        {!editing ? (
          <div className="mys-row-actions">
            <button type="button" className={`mys-btn${open === 'csv' ? ' is-active' : ''}`} onClick={() => setOpen(open === 'csv' ? '' : 'csv')}>Upload CSV</button>
            <button type="button" className={`mys-btn${open === 'hook' ? ' is-active' : ''}`} onClick={() => setOpen(open === 'hook' ? '' : 'hook')}>Webhook</button>
            <button type="button" className="mys-btn" onClick={() => setEditing(true)}>Edit</button>
            <button type="button" className="mys-btn is-danger" onClick={remove}>Delete</button>
          </div>
        ) : null}
      </div>
      {error ? <p className="mys-error" role="alert">{error}</p> : null}
      {open === 'csv' ? <CsvImport strategy={strategy} onDone={() => { setOpen(''); onChanged(); }} /> : null}
      {open === 'hook' ? <WebhookPanel strategy={strategy} onChange={onChanged} /> : null}
    </li>
  );
}

export default function MyStrategiesManager({ strategies, onClose, onChanged }) {
  const [name, setName] = useState('');
  const [capital, setCapital] = useState('10000');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) { nameRef.current?.focus(); return; }
    setBusy(true);
    setError('');
    try {
      await createMyStrategy(name.trim(), Number(capital) || 10000);
      setName('');
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <InsightModal title="My strategies" subtitle="Your own strategies, from a backtest CSV or a webhook. Only you can see them." onClose={onClose} wide>
      <div className="mys">
        <form className="mys-add" onSubmit={add}>
          <input ref={nameRef} placeholder="Strategy name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label="Strategy name" />
          <label>Starting balance <input type="number" min="1" value={capital} onChange={(e) => setCapital(e.target.value)} /></label>
          <button type="submit" className="mys-btn is-primary" disabled={busy}>Add strategy</button>
        </form>
        {error ? <p className="mys-error" role="alert">{error}</p> : null}
        {strategies.length ? (
          <ul className="mys-list">
            {strategies.map((s) => <StrategyRow key={s.id} strategy={s} onChanged={onChanged} />)}
          </ul>
        ) : (
          <p className="mys-muted mys-empty">No strategies yet. Add one, then upload a CSV or turn on its webhook.</p>
        )}
        <p className="mys-muted mys-foot">
          These are personal and appear only in your Results and Portfolio Simulator. To add a strategy to a company, ask one of its Brand Admins.
        </p>
      </div>
    </InsightModal>
  );
}
